import { Button } from "@svea/design-system-components-react";
import { formatSsn, Stack, Text, useText } from "@sveabank/cc-common";
import { useState } from "react";
import Card from "../../components/card";
import { useApplicationId } from "../../hooks/useApplicationId";
import {
	applicationsApiSlice,
	useAssignFullAccessUsersMutation,
	useRemoveFullAccessUsersMutation,
} from "../../services/application";
import { useAppDispatch } from "../../store";
import { useApplicationSettings } from "../application-settings/hooks";

type Props = {
	firstName: string | null;
	lastName: string | null;
	nationalId: string;
	isAdded?: boolean;
	editable?: boolean;
};

const UserActions = ({ nationalId, isAdded }: { nationalId: string; isAdded?: boolean }) => {
	const { fullAccessUsers } = useApplicationSettings();
	const applicationId = useApplicationId();
	const dispatch = useAppDispatch();
	const [isUpdating, setIsUpdating] = useState(false);
	const [assignUser, { isLoading: assignLoading }] = useAssignFullAccessUsersMutation();
	const [removeUser, { isLoading: removeLoading }] = useRemoveFullAccessUsersMutation();

	const handleAddUser = async () => {
		const addedUsers = fullAccessUsers.map((a) => a.nationalId);
		if (fullAccessUsers.length) {
			await removeUser(applicationId);
		}
		const users = { nationalIds: [nationalId, ...addedUsers] };
		await assignUser({ applicationId, fullAccessUsers: users });
	};

	const handleRemoveUser = async () => {
		setIsUpdating(true);
		try {
			const nationalIds = fullAccessUsers
				.filter((f) => f.nationalId !== nationalId)
				.map((m) => m.nationalId);

			await removeUser(applicationId);
			if (nationalIds.length) {
				await assignUser({
					applicationId,
					fullAccessUsers: { nationalIds },
				});
			} else {
				dispatch(applicationsApiSlice.util.invalidateTags(["FullAccessUsers"]));
			}
		} finally {
			setIsUpdating(false);
		}
	};

	const isLoading = isUpdating || assignLoading || removeLoading;
	const testId = isAdded ? "remove-btn" : "add-btn";
	const callback = isAdded ? handleRemoveUser : handleAddUser;

	return (
		<Button
			aria-label={isAdded ? "Ta bort" : "Lägg till"}
			variant="icon"
			icon={isAdded ? "trash" : "plusCircle"}
			onPress={() => callback()}
			isDisabled={isLoading}
			data-testid={testId}
		/>
	);
};

const UserCard = ({ firstName, lastName, nationalId, editable = true, isAdded }: Props) => {
	const [t, texts] = useText();

	return (
		<Card classNames="bg-background-secondary">
			<Stack gap={4} direction="row">
				{editable && <UserActions nationalId={nationalId} isAdded={isAdded} />}
				<Stack>
					<Stack direction="row" gap={2}>
						<Text bold>
							{firstName || t(texts.common.noName)} {lastName || t(texts.common.noName)}
						</Text>
						<Text>{!editable && ` ${t(texts.common.you)}`}</Text>
					</Stack>
					<Text>{formatSsn(nationalId)}</Text>
				</Stack>
			</Stack>
		</Card>
	);
};

export default UserCard;
